import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import QrScanner from 'qr-scanner';
import {
  Camera,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  RotateCw,
  Keyboard,
  QrCode
} from 'lucide-react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

export const forceStopAllCameras = () => {
  try {
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach((video) => {
      if (video.srcObject && video.srcObject instanceof MediaStream) {
        video.srcObject.getTracks().forEach((track) => {
          if (track.readyState === 'live') track.stop();
        });
        video.srcObject = null;
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    });
  } catch (err) {
    console.error('forceStopAllCameras error', err);
  }
};

export default function QRScanner({ onScanComplete, onBackToDashboard, className }) {
  const [scanStatus, setScanStatus] = useState('idle');
  const [scannedValue, setScannedValue] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [isManualMode, setIsManualMode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cameraError, setCameraError] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);

  const initializeQRScanner = async () => {
    console.log('📱 Initializing QR Scanner...');
    try {
      if (!videoRef.current) {
        console.error('❌ Video element not available');
        setCameraError(true);
        setErrorMessage('Video element not ready. Please refresh the page.');
        return;
      }

      // Stop any existing scanner with proper cleanup
      if (qrScannerRef.current) {
        console.log('🛑 Stopping existing QR scanner...');
        try {
          qrScannerRef.current.stop();
          qrScannerRef.current.destroy();
        } catch (error) {
          console.warn('⚠️ Error during existing scanner cleanup:', error);
        }
        qrScannerRef.current = null;
      }

      // Clear video element before creating new scanner
      if (videoRef.current.srcObject) {
        videoRef.current.srcObject = null;
        videoRef.current.load();
      }

      console.log('🎥 Creating new QR scanner instance...');
      const qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          console.log('🎯 QR Code detected:', result.data);
          handleQRDetected(result.data);
        },
        {
          onDecodeError: (err) => {
            // Don't log decode errors as they're expected when no QR code is visible
            // console.log('QR decode error:', err);
          },
          preferredCamera: 'environment', // Use back camera on mobile
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );

      qrScannerRef.current = qrScanner;
      
      console.log('▶️ Starting QR scanner...');
      await qrScanner.start();
      
      console.log('✅ QR Scanner initialized successfully');
      setScanStatus('scanning');
      setIsScanning(true);
      setCameraError(false);
      setErrorMessage('');
      
    } catch (err) {
      console.error('❌ QR Scanner initialization error:', err);
      setCameraError(true);
      setScanStatus('error');
      setIsScanning(false);
      
      if (err && err.name === 'NotAllowedError') {
        setErrorMessage('Camera permission denied. Please allow camera access and refresh.');
      } else if (err && err.name === 'NotFoundError') {
        setErrorMessage('No camera found on this device.');
      } else {
        setErrorMessage(`Camera access failed: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const handleQRDetected = (qrData) => {
    console.log('📊 Processing QR data:', qrData);
    
    // Clean and validate the QR data
    const cleanedData = qrData.trim().toUpperCase();
    
    if (cleanedData.length >= 2) {
      console.log('🎯 QR CODE ACCEPTED:', cleanedData);
      setScannedValue(cleanedData);
      setScanStatus('success');
      setErrorMessage('');
      
      // Stop scanning when QR code is found
      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        setIsScanning(false);
      }
    } else {
      console.log('⚠️ QR data too short, continuing scan...');
    }
  };

  const handleRetryScan = async () => {
    console.log('🔄 Retry scan requested...');
    setScannedValue('');
    setScanStatus('idle');
    setErrorMessage('');
    setCameraError(false);
    setIsScanning(false);
    
    // Stop current scanner with a proper cleanup delay
    stopQRScanner();
    
    // Add a small delay to ensure proper cleanup before reinitializing
    setTimeout(async () => {
      console.log('🎬 Reinitializing QR scanner after cleanup...');
      await initializeQRScanner();
    }, 300);
  };

  const handleManualSubmit = () => {
    if (manualInput && manualInput.trim()) {
      const cleanInput = manualInput.trim().toUpperCase();
      setScannedValue(cleanInput);
      setScanStatus('success');
      setErrorMessage('');
    } else {
      setErrorMessage('Please enter a serial number');
    }
  };

  const toggleInputMode = () => {
    stopQRScanner();
    setScannedValue('');
    setScanStatus('idle');
    setErrorMessage('');
    setManualInput('');
    setCameraError(false);
    setIsScanning(false);
    setIsManualMode((s) => !s);
  };

  const stopQRScanner = () => {
    if (qrScannerRef.current) {
      console.log('🛑 Stopping QR scanner...');
      try {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
      } catch (error) {
        console.warn('⚠️ Error during QR scanner cleanup:', error);
      }
      qrScannerRef.current = null;
      setIsScanning(false);
    }
    
    // Force stop all cameras as backup
    forceStopAllCameras();
    
    // Additional cleanup - clear video element src
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
  };

  // Initialize QR scanner when component mounts or manual mode changes
  useEffect(() => {
    if (!isManualMode && scanStatus !== 'success') {
      initializeQRScanner();
    }
    
    return () => {
      stopQRScanner();
    };
  }, [isManualMode]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      console.log('🔄 QR Scanner component unmounting, cleaning up...');
      stopQRScanner();
    };
  }, []);

  return (
    <section className={cn('min-h-screen bg-gray-50 p-4 flex items-center justify-center', className)}>
      <div className="w-full max-w-2xl">
        <Card className="bg-white shadow-xl">
          <CardContent className="p-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
              <div className="flex items-center justify-between mb-4">
                <Button variant="ghost" onClick={onBackToDashboard} className="text-white hover:bg-white/20 p-2">
                  <ArrowLeft className="h-5 w-5" />
                </Button>

                <h2 className="text-xl font-bold">QR Code Scanner</h2>

                <Button
                  variant="ghost"
                  onClick={toggleInputMode}
                  className="text-white hover:bg-white/20 p-2"
                  title={isManualMode ? 'Switch to Camera' : 'Switch to Manual Input'}
                >
                  {isManualMode ? <Camera className="h-5 w-5" /> : <Keyboard className="h-5 w-5" />}
                </Button>
              </div>
              
              {/* Status indicator */}
              {!isManualMode && (
                <div className="flex items-center gap-2 text-sm">
                  <div className={cn(
                    'w-2 h-2 rounded-full',
                    isScanning ? 'bg-green-400' : 
                    cameraError ? 'bg-red-400' : 
                    'bg-yellow-400'
                  )} />
                  <span className="text-white/90">
                    {isScanning ? 'Scanning for QR codes...' : 
                     cameraError ? 'Camera Error' : 
                     'Initializing camera...'}
                  </span>
                </div>
              )}
            </div>

            {/* Main Content */}
            <div className="p-6">
              {!isManualMode ? (
                <div className="space-y-4">
                  <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: '400px' }}>
                    {scanStatus === 'success' ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-green-50 p-8 z-10">
                        <CheckCircle className="h-16 w-16 text-green-600 mb-4" />
                        <p className="text-lg font-semibold text-green-800 mb-2">QR Code Detected!</p>

                        <div className="bg-white border-2 border-green-300 rounded-lg p-4 mb-6 max-w-xs w-full">
                          <p className="text-sm text-gray-600 mb-1">Scanned Data:</p>
                          <textarea
                            value={scannedValue}
                            onChange={(e) => setScannedValue(e.target.value.toUpperCase())}
                            className="w-full h-20 text-lg font-mono text-gray-900 border-0 p-0 focus:ring-0 bg-transparent resize-none"
                            placeholder="Edit scanned data..."
                            rows={3}
                          />
                        </div>

                        <div className="flex gap-4">
                          <Button onClick={() => onScanComplete(scannedValue)} className="bg-green-600 hover:bg-green-700 text-white px-6 py-3">
                            Continue
                          </Button>

                          <Button onClick={handleRetryScan} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3">
                            <RotateCw className="h-5 w-5 mr-2" />
                            Scan Again
                          </Button>
                        </div>

                        <p className="text-xs text-gray-500 mt-4 text-center">Verify the code above is correct before continuing</p>
                      </div>
                    ) : cameraError ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 z-10">
                        <AlertCircle className="h-16 w-16 text-red-600 mb-4" />
                        <p className="text-lg font-semibold text-red-800">Scanner Error</p>
                        <p className="text-sm text-red-600 mt-2 text-center px-4">{errorMessage}</p>
                        <Button onClick={handleRetryScan} className="mt-4 bg-red-600 hover:bg-red-700 text-white">
                          <RotateCw className="h-4 w-4 mr-2" />
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <div className="relative w-full h-full">
                        <video 
                          ref={videoRef} 
                          className="w-full h-full object-cover" 
                          autoPlay 
                          muted 
                          playsInline 
                        />
                        
                        {/* QR Code scanning overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-64 h-64 border-2 border-green-400 rounded-lg relative">
                            {/* Corner indicators */}
                            <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400"></div>
                            <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400"></div>
                            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400"></div>
                            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400"></div>
                            
                            {/* Center QR icon */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <QrCode className="h-8 w-8 text-green-400 opacity-50" />
                            </div>
                          </div>
                        </div>
                        
                        {/* Scanning indicator */}
                        {isScanning && (
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                              <span className="text-sm">Scanning for QR codes...</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {errorMessage && !cameraError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                        <p className="text-red-800 text-sm">{errorMessage}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <Keyboard className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-6">Enter the serial number manually</p>

                    <div className="max-w-sm mx-auto space-y-4">
                      <Input
                        type="text"
                        value={manualInput}
                        onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                        placeholder="Enter serial number"
                        className="text-center text-lg font-mono"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleManualSubmit();
                        }}
                      />

                      {errorMessage && <p className="text-red-600 text-sm">{errorMessage}</p>}

                      {scanStatus === 'success' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                          <p className="text-green-800 font-semibold">Success!</p>
                          <p className="text-green-600 text-sm mt-1">{scannedValue}</p>
                        </div>
                      )}

                      <Button 
                        onClick={scanStatus === 'success' ? () => onScanComplete(scannedValue) : handleManualSubmit} 
                        disabled={scanStatus !== 'success' && !manualInput.trim()} 
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t">
              <p className="text-xs text-gray-500 text-center">
                {isManualMode
                  ? 'Type the serial number as it appears on the unit or QR code'
                  : isScanning 
                    ? 'Position QR code within the scanning area. The scanner will automatically detect and read QR codes.'
                    : 'Initializing camera for QR code scanning...'}
              </p>
              {!isManualMode && isScanning && (
                <p className="text-xs text-blue-600 text-center mt-1">💡 For best results: Hold device steady and ensure QR code is clearly visible</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}